// Port of PROTO lore.html: "Epic Clan Lore" — a music player in a black
// cloth banner driving SoundCloud's hidden widget over postMessage (the
// widget's own protocol), with our own transport controls. If the widget
// never answers within 10s (blocked, offline, headless) we fall back to a
// plain link, exactly as the prototype's inline script did.
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { BannerCanvas } from '../BannerCanvas.js';
import { Opener } from '../Opener.js';
import './LorePage.css';

const WIDGET_ORIGIN = 'https://w.soundcloud.com';
const PLAYLIST_SRC =
  'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/1983125352&auto_play=false&visual=false&show_artwork=false&buying=false&sharing=false';
const SOUNDCLOUD_LINK = 'https://soundcloud.com/fullsails/sets/pirate-songs-by-hartje';
const ROMAN = [
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX',
];

interface SoundCloudSound {
  title?: string;
  duration?: number;
}

interface WidgetMessage {
  method: string;
  value?: unknown;
}

function formatTime(ms: number): string {
  const s = Math.floor((ms || 0) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function LorePage() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const soundsRef = useRef<SoundCloudSound[]>([]);
  const draggingRef = useRef(false);
  const durationRef = useRef(0);

  const [sounds, setSounds] = useState<SoundCloudSound[]>([]);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [fallback, setFallback] = useState(false);
  const [seek, setSeek] = useState(0);
  const [timeText, setTimeText] = useState('0:00');

  // Always-fresh mirrors for the mount-once message handler below (it closes
  // over these refs, not the state, so it never reads a stale value).
  soundsRef.current = sounds;

  function send(method: string, value?: unknown): void {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(JSON.stringify(value === undefined ? { method } : { method, value }), WIDGET_ORIGIN);
  }

  useEffect(() => {
    const ifr = iframeRef.current;
    if (!ifr) return undefined;
    let ready = false;

    function onMessage(e: MessageEvent): void {
      if (e.origin !== WIDGET_ORIGIN || e.source !== ifr?.contentWindow) return;
      let data: WidgetMessage;
      try {
        data = JSON.parse(e.data as string) as WidgetMessage;
      } catch {
        return;
      }
      switch (data.method) {
        case 'ready':
          ready = true;
          setFallback(false);
          (['play', 'pause', 'finish', 'playProgress'] as const).forEach((ev) => send('addEventListener', ev));
          send('getSounds');
          break;
        case 'getSounds':
          setSounds((data.value as SoundCloudSound[]) || []);
          break;
        case 'getCurrentSoundIndex': {
          const idx = (data.value as number) || 0;
          setCurrent(idx);
          durationRef.current = soundsRef.current[idx]?.duration || 0;
          break;
        }
        case 'play':
          setPlaying(true);
          send('getCurrentSoundIndex');
          send('getSounds');
          break;
        case 'pause':
        case 'finish':
          setPlaying(false);
          send('getCurrentSoundIndex');
          break;
        case 'playProgress': {
          const value = data.value as { relativePosition: number; currentPosition: number } | undefined;
          if (value) {
            if (!draggingRef.current) setSeek(Math.round(value.relativePosition * 1000));
            setTimeText(formatTime(value.currentPosition));
            if (value.relativePosition > 0) durationRef.current = value.currentPosition / value.relativePosition;
          }
          break;
        }
        default:
          break;
      }
    }

    window.addEventListener('message', onMessage);
    // The widget never answered (blocked or offline): point to SoundCloud instead.
    const timeoutId = window.setTimeout(() => {
      if (!ready) setFallback(true);
    }, 10000);

    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timeoutId);
    };
  }, []);

  function handlePick(i: number): void {
    setCurrent(i);
    send('skip', i);
  }

  function handleSeekChange(e: ChangeEvent<HTMLInputElement>): void {
    draggingRef.current = true;
    setSeek(Number(e.target.value));
  }

  function commitSeek(): void {
    draggingRef.current = false;
    if (durationRef.current) send('seekTo', (durationRef.current * seek) / 1000);
  }

  const name = (i: number): string => sounds[i]?.title || `Song ${i + 1}`;
  const titleContent =
    sounds.length > 0 ? (
      name(current)
    ) : fallback ? (
      <a href={SOUNDCLOUD_LINK}>Listen on SoundCloud ↗</a>
    ) : (
      'Loading the songs…'
    );
  const bandClassName = ['player-band', playing ? 'playing' : '', fallback ? 'pl-off' : ''].filter(Boolean).join(' ');

  return (
    <>
      <Opener lines={['Epic', 'Clan Lore']} lede="Music & Stories" />
      <section className={bandClassName} aria-labelledby="pl-h">
        <BannerCanvas kind="cloth" />
        <div className="wrap player">
          <div className="pl-now">
            <h2 className="label pl-h" id="pl-h">
              Pirate songs by Hartje
            </h2>
            <p className="pl-title" id="pl-title" aria-live="polite">
              {titleContent}
            </p>
            <div className="pl-ctrl">
              <button type="button" className="pl-btn" onClick={() => send('prev')} aria-label="Previous song">
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M6 5h2v14H6zM20 5v14L9 12z" fill="currentColor" />
                </svg>
              </button>
              <button
                type="button"
                className="pl-btn pl-play"
                onClick={() => send(playing ? 'pause' : 'play')}
                aria-label={playing ? 'Pause' : 'Play'}
              >
                <svg className="i-play" viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
                  <path d="M8 5v14l11-7z" fill="currentColor" />
                </svg>
                <svg className="i-pause" viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
                  <path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor" />
                </svg>
              </button>
              <button type="button" className="pl-btn" onClick={() => send('next')} aria-label="Next song">
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M16 5h2v14h-2zM4 5v14l11-7z" fill="currentColor" />
                </svg>
              </button>
              <span className="pl-time" id="pl-time">
                {timeText}
              </span>
            </div>
            <input
              className="pl-seek"
              id="pl-seek"
              type="range"
              min={0}
              max={1000}
              value={seek}
              aria-label="Seek"
              style={{ ['--p' as unknown as string]: `${seek / 10}%` }}
              onChange={handleSeekChange}
              onMouseUp={commitSeek}
              onTouchEnd={commitSeek}
              onKeyUp={commitSeek}
            />
          </div>
          <ol className="pl-list" id="pl-list" data-lenis-prevent>
            {sounds.map((s, i) => (
              <li key={i} className={i === current ? 'on' : undefined}>
                <button type="button" onClick={() => handlePick(i)}>
                  <span className="n">{ROMAN[i] ?? String(i + 1)}</span>
                  <span>{name(i)}</span>
                  <span className="d">{s.duration ? formatTime(s.duration) : ''}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
        <iframe
          ref={iframeRef}
          className="pl-engine"
          id="pl-engine"
          title="SoundCloud player"
          tabIndex={-1}
          aria-hidden="true"
          allow="autoplay"
          src={PLAYLIST_SRC}
        />
      </section>
      <section className="wrap pl-src">
        {/* Named "pl-go", not the prototype's literal "go": that class is
            defined only in HomePage.css (the home doors) in build.py — on
            lore.html itself it carries no rules, so the link renders as a
            plain underlined anchor. Our page CSS is global (no CSS modules
            here), so reusing "go" would pick up Home's styling by accident
            (build-plan: pages must scope/prefix classes to avoid collisions). */}
        <a className="pl-go" href={SOUNDCLOUD_LINK}>
          Listen on SoundCloud <span aria-hidden="true">↗</span>
        </a>
      </section>
    </>
  );
}
